import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, Pagination, Table } from "flowbite-react";
import { FaArrowsLeftRight, FaUserShield } from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery, useQueryClient } from "react-query";
import { ADMIN } from "../../config/apiConfig";
import { formatToReal } from "../../utils/utils";
import AutoCompleteInput from "../inputs/Autocomplete";
import EmptyTable from "./EmptyTable";
import { BsFillBarChartFill } from "react-icons/bs";
import DrawerLoading from "../DrawerLoading";
import TableLoading from "../TableLoading";
import { FaRegEdit, FaUsers } from "react-icons/fa";
import UserForm from "../forms/UserForm";
import { Tooltip } from "react-tooltip";
import { IoMdAdd, IoMdSearch } from "react-icons/io";
import { LuFilter, LuFilterX } from "react-icons/lu";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const AdminUsersTable = ({ isActive = true }) => {
  const queryClient = useQueryClient();
  const { user } = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPriceSum, setTotalPriceSum] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchName, setSearchName] = useState("");
  const [nameParam, setNameParam] = useState("");
  const onPageChange = (page) => setCurrentPage(page);
  const [selectedCustomer, setSelectedCustomer] = useState({
    label: "Todos os clientes",
    value: 0,
  });
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDrawerEditUser, setIsDrawerEditUser] = useState(false);
  const [isDrawerOpenUser, setIsDrawerOpenUser] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [renderFilters, setRenderFilters] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState(null);

  const handleImpersonateUser = async (user) => {
    if (!user?.id) return;
    try {
      setImpersonatingId(user.id);

      // pega token do backend
      const { data } = await axios.get(`${ADMIN.GET_TOKEN}?userId=${user.id}`);
      const token = data?.data?.token || data?.token;
      if (!token) throw new Error("Token de impersonação não retornado.");

      // seta no localStorage da aba corrente
      localStorage.setItem("token", token);

      // redireciona para a home (ou dashboard)
      window.location.href = "/";
    } catch (err) {
      console.error(err);
      alert(err?.message || "Erro ao personificar usuário.");
    } finally {
      setImpersonatingId(null);
    }
  };

  useEffect(() => {
    if (showFilters) {
      setRenderFilters(true); // mostra o conteúdo imediatamente
    } else {
      // espera a animação terminar antes de esconder
      const timeout = setTimeout(() => setRenderFilters(false), 500); // mesmo tempo da transition
      return () => clearTimeout(timeout);
    }
  }, [showFilters]);

  const {
    isLoading: isLoadingCustomers,
    error: errorListCustomers,
    data: listCustomers,
  } = useQuery(
    ["listCustomers"],
    async () => {
      try {
        const { data } = await axios.get(
          `${ADMIN.GET_CUSTOMERS}?itemsPerPage=-1`
        );

        if (!data?.data?.data) {
          throw new Error("Erro ao buscar clientes");
        }

        let customers = data.data.data.map((customer) => ({
          label:
            customer.typeCustomer === "CPF"
              ? customer.name
              : customer.socialReason,
          value: customer.id,
        }));

        customers = [{ label: "Todos os clientes", value: 0 }, ...customers];

        return customers;
      } catch (error) {
        throw new Error(`Erro ao buscar clientes: ${error.message}`);
      }
    },
    { enabled: isActive }
  );

  const {
    isLoading: isLoadingListUsers,
    error: errorListUsers,
    data: listUsers,
  } = useQuery(
    [
      "listUsers",
      currentPage,
      selectedStatuses,
      startDate,
      endDate,
      selectedCustomer,
      nameParam,
    ],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_USERS}?page=${currentPage}&status=${selectedStatuses}&companyId=${selectedCustomer?.value}&startDate=${startDate}&endDate=${endDate}&search=${nameParam}`
      );

      setTotalPages(data.data.totalPages || 1);
      setTotalCount(data.data.totalCount || 0);

      return data.data.data;
    },
    { enabled: isActive }
  );

  const appliedFilterSummary = useMemo(() => {
    const items = [];
    if (selectedCustomer?.value && selectedCustomer?.value !== 0) {
      items.push(`Cliente: ${selectedCustomer?.label || "-"}`);
    }
    if (startDate || endDate) {
      const startLabel = startDate
        ? moment(startDate).format("DD/MM/YYYY")
        : "—";
      const endLabel = endDate ? moment(endDate).format("DD/MM/YYYY") : "—";
      items.push(`Período: ${startLabel} a ${endLabel}`);
    }
    if (selectedStatuses.length > 0) {
      const labels = selectedStatuses
        .map((status) => (status === 1 ? "Ativo" : "Bloqueado"))
        .join(", ");
      items.push(`Status: ${labels}`);
    }
    if (nameParam) {
      items.push(`Busca: ${nameParam}`);
    }
    return items;
  }, [
    endDate,
    nameParam,
    selectedCustomer?.label,
    selectedCustomer?.value,
    selectedStatuses,
    startDate,
  ]);

  const hasActiveFilters = appliedFilterSummary.length > 0;

  const handleSearchName = (e) => {
    e.preventDefault();
    setNameParam(searchName);
  };

  const handleCheckboxChange = (status, setState) => {
    setState((prev) =>
      prev.includes(status)
        ? prev.filter((item) => item !== status)
        : [...prev, status]
    );
  };

  // const handleSearchName = (e) => {
  //   e.preventDefault();
  //   setNameParam(searchName);
  // };

  const handleEditCustomer = (user) => {
    setSelectedUser(user);
    setIsDrawerEditUser((prevState) => !prevState);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <Button
          size="xs"
          onClick={() => setShowFilters((prev) => !prev)}
          className="bg-white text-slate-700 border border-slate-200 shadow-sm hover:bg-gray-50 hover:text-slate-900 transition-all duration-300 hover:text-white"
        >
          <div className="flex items-center gap-2 transition-all duration-300">
            <span>{showFilters ? "Esconder filtros" : "Mostrar filtros"}</span>
            <span className={`transition-transform duration-300 `}>
              {!showFilters ? <LuFilter /> : <LuFilterX />}
            </span>
          </div>
        </Button>
      </div>
      <div className="flex flex-col gap-4">
        {showFilters && (
          <div
            className={`transition-all duration-500 ease-in-out overflow-hidden bg-white border border-slate-200 rounded-xl shadow-sm`}
          >
            {renderFilters && (
              <div className="transition-opacity duration-500">
                {isLoadingCustomers && <DrawerLoading />}
                <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-slate-200 bg-gray-50">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Filtros Avançados
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-6 gap-3 px-3 py-3">
                  <div className="flex flex-col md:col-span-3">
                    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Cliente
                    </label>
                    <div className="h-9">
                      <AutoCompleteInput
                        options={listCustomers}
                        label=""
                        onChange={(customer) => setSelectedCustomer(customer)}
                        value={selectedCustomer?.value}
                        isLoading={isLoadingCustomers}
                        className="h-full"
                      />
                    </div>
                    {errorListCustomers && (
                      <Alert color="failure" className="mt-2">
                        Ocorreu um erro ao buscar clientes
                      </Alert>
                    )}
                  </div>

                  <div className="flex flex-col md:col-span-3">
                    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Período de criação
                    </label>
                    <div className="flex items-center gap-2 h-12">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          if (moment(e.target.value).isAfter(moment(endDate))) {
                            setEndDate(e.target.value);
                          }
                        }}
                        className="w-full h-full bg-white border border-slate-300 text-xs rounded-md px-2.5 py-2 focus:ring-slate-500 focus:border-slate-500"
                      />
                      <div className="text-[11px] text-slate-500">até</div>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                        }}
                        min={startDate}
                        className="w-full h-full bg-white border border-slate-300 text-xs rounded-md px-2.5 py-2 focus:ring-slate-500 focus:border-slate-500"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col md:col-span-6 mt-3">
                    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Status
                    </label>
                    <div className="flex gap-2 h-9">
                      <button
                        type="button"
                        onClick={() =>
                          handleCheckboxChange(1, setSelectedStatuses)
                        }
                        className={`h-full px-3 rounded-md text-xs font-semibold border transition w-full ${
                          selectedStatuses.includes(1)
                            ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        Ativo
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleCheckboxChange(0, setSelectedStatuses)
                        }
                        className={`h-full px-3 rounded-md text-xs font-semibold border transition w-full ${
                          selectedStatuses.includes(0)
                            ? "bg-rose-600 text-white border-rose-600 shadow-sm"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        Bloqueado
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex-1">
          <div className="mt-3">
            <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gradient-to-r from-white to-gray-50 px-4 py-2 shadow-sm md:flex-row md:items-center md:justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 shadow-inner">
                  <BsFillBarChartFill className="text-xl" />
                </div>
                <p className="text-base font-semibold text-gray-900">
                  Resultado da busca
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 justify-end text-xs">
                {hasActiveFilters ? (
                  <>
                    <span className="uppercase tracking-wide text-gray-500">
                      Filtros ativos
                    </span>
                    {appliedFilterSummary.slice(0, 4).map((item, index) => (
                      <span
                        key={`${item}-${index}`}
                        className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200 shadow-sm"
                      >
                        {item}
                      </span>
                    ))}
                    {appliedFilterSummary.length > 4 && (
                      <span className="px-2 py-1 rounded-full bg-gray-50 text-gray-500 text-xs font-medium border border-dashed border-gray-300">
                        +{appliedFilterSummary.length - 4} oculto(s)
                      </span>
                    )}
                  </>
                ) : (
                  <span className="uppercase tracking-wide text-gray-500">
                    Nenhum filtro aplicado
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <div className="flex gap-4 pb-3 min-w-[420px]">
              <div className="min-w-[200px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-[#1E429F] to-[#3B82F6] relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">Total de usuários</p>
                    <h2 className="text-2xl font-bold mt-1">
                      {!isLoadingListUsers ? (
                        `${totalCount}`
                      ) : (
                        <div className="animate-pulse h-6 bg-white/30 rounded w-24" />
                      )}
                    </h2>
                  </div>
                  <FaUsers className="text-white text-4xl opacity-20" />
                </div>
              </div>
              <div className="min-w-[200px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-[#0F766E] to-[#14B8A6] relative">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">
                      Total de usuários ativos
                    </p>
                    <h2 className="text-2xl font-bold mt-1">
                      {!isLoadingListUsers ? (
                        `${totalCount}`
                      ) : (
                        <div className="animate-pulse h-6 bg-white/30 rounded w-24" />
                      )}
                    </h2>
                  </div>
                  <FaUserShield className="text-white text-4xl opacity-20" />
                </div>
              </div>
            </div>
          </div>

          <form
            className="flex gap-1 mt-5 w-full pl-3 pr-2 py-2 md:py-1 rounded-md shadow-sm bg-gray-300"
            onSubmit={handleSearchName}
          >
            <input
              type="text"
              placeholder="Busque por nome do usuário..."
              className="border-0 py-1 text-sm mt-1 focus:outline-none focus:ring-0 focus:border-gray-300 px-0 w-full bg-gray-300 placeholder:text-gray-700"
              onChange={(e) => setSearchName(e.target.value)}
              value={searchName}
            />
            <Button
              size="sm"
              className="flex items-center bg-secondary w-20"
              type="submit"
            >
              <IoMdSearch />
            </Button>
          </form>

          {listUsers?.length > 0 || isLoadingListUsers ? (
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow mt-0 md:mt-5">
              {/* Mobile: Cards */}
              <div className="md:hidden p-3 space-y-3">
                {!isLoadingListUsers
                  ? (listUsers?.length ?? 0) > 0
                    ? listUsers.map((u, index) => {
                        const cardTooltipId = `tooltip-edit-user-card-${
                          u?.id ?? index
                        }`;
                        const cardImpId = `imp-card-${u?.id ?? index}`;
                        const customerName =
                          u?.Company?.typeCustomer === "CPF"
                            ? u?.Company?.name
                            : u?.Company?.socialReason;

                        return (
                          <div
                            key={index}
                            className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div
                                  className="text-sm font-semibold text-gray-900 truncate"
                                  title={u.name}
                                >
                                  {u.name}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {customerName || "-"}
                                </div>
                              </div>
                              {u.accessCode ? (
                                <div className="flex items-center gap-2">
                                  <code className="rounded bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-800">
                                    {u.accessCode}
                                  </code>
                                  <button
                                    type="button"
                                    className="rounded px-1.5 py-0.5 text-[11px] border border-gray-200 hover:bg-gray-100"
                                    onClick={() =>
                                      navigator.clipboard.writeText(
                                        u.accessCode
                                      )
                                    }
                                    aria-label="Copiar código"
                                    title="Copiar"
                                  >
                                    Copiar
                                  </button>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-xs">—</span>
                              )}
                            </div>

                            <div className="mt-2">
                              {u.email ? (
                                <a
                                  href={`mailto:${u.email}`}
                                  className="text-xs text-gray-800 underline-offset-2 hover:underline block truncate"
                                  title={u.email}
                                >
                                  {u.email}
                                </a>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </div>

                            <div className="mt-3 flex items-center gap-2">
                              <Tooltip
                                id={cardTooltipId}
                                content="Editar usuário"
                              />
                              <button
                                type="button"
                                data-tooltip-id={cardTooltipId}
                                onClick={() => handleEditCustomer(u)}
                                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                                aria-label={`Editar ${u?.name ?? "usuário"}`}
                              >
                                <FaRegEdit className="text-gray-500" />
                                Editar
                              </button>

                              {u?.id != 1 && (
                                <>
                                  <Tooltip
                                    id={cardImpId}
                                    content="Personificar usuário"
                                  />
                                  <button
                                    type="button"
                                    data-tooltip-id={cardImpId}
                                    onClick={() => handleImpersonateUser(u)}
                                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
                                    aria-label={`Personificar ${
                                      u?.name ?? "usuário"
                                    }`}
                                    disabled={impersonatingId === u?.id}
                                  >
                                    {impersonatingId === u?.id ? (
                                      <AiOutlineLoading3Quarters className="animate-spin" />
                                    ) : (
                                      <FaUserShield />
                                    )}
                                    <span>Personificar</span>
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })
                    : null
                  : Array.from({ length: 4 }).map((_, idx) => (
                      <div
                        key={idx}
                        className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm animate-pulse"
                      >
                        <div className="h-4 bg-gray-100 rounded w-1/2" />
                        <div className="mt-2 h-3 bg-gray-100 rounded w-1/3" />
                        <div className="mt-3 h-3 bg-gray-100 rounded w-2/3" />
                        <div className="mt-3 h-7 bg-gray-100 rounded w-1/2" />
                      </div>
                    ))}
              </div>

              {/* Desktop: Tabela */}
              <div className="hidden md:block overflow-x-auto">
                <Table hoverable striped className="text-xs xl:text-sm">
                  <Table.Head className="sticky top-0 bg-gray-100">
                    <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                      Código de acesso
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                      Usuário
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                      E-mail
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                      Cliente
                    </Table.HeadCell>
                    <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                      Ações
                    </Table.HeadCell>
                  </Table.Head>

                  <Table.Body className="divide-y">
                    {!isLoadingListUsers ? (
                      (listUsers?.length ?? 0) > 0 ? (
                        listUsers.map((u, index) => {
                          const tooltipId = `tooltip-edit-user-table-${
                            u?.id ?? index
                          }`;
                          const impId = `imp-table-${u?.id ?? index}`;
                          const customerName =
                            u?.Company?.typeCustomer === "CPF"
                              ? u?.Company?.name
                              : u?.Company?.socialReason;

                          return (
                            <Table.Row
                              key={index}
                              className="bg-white focus-within:bg-indigo-50/40 hover:bg-indigo-50/40"
                            >
                              <Table.Cell className="text-center py-2">
                                {u.accessCode ? (
                                  <span className="inline-flex items-center gap-2">
                                    <code className="rounded bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-800">
                                      {u.accessCode}
                                    </code>
                                    <button
                                      type="button"
                                      className="rounded px-1.5 py-0.5 text-[11px] border border-gray-200 hover:bg-gray-100"
                                      onClick={() =>
                                        navigator.clipboard.writeText(
                                          u.accessCode
                                        )
                                      }
                                      aria-label="Copiar código"
                                      title="Copiar"
                                    >
                                      Copiar
                                    </button>
                                  </span>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </Table.Cell>

                              <Table.Cell className="text-center py-2 font-medium text-gray-900">
                                <span
                                  className="truncate inline-block max-w-[180px] md:max-w-[240px]"
                                  title={u.name}
                                >
                                  {u.name}
                                </span>
                              </Table.Cell>

                              <Table.Cell className="text-center py-2">
                                {u.email ? (
                                  <a
                                    href={`mailto:${u.email}`}
                                    className="text-gray-800 underline-offset-2 hover:underline"
                                    title={u.email}
                                  >
                                    <span className="truncate inline-block max-w-[200px] md:max-w-[260px]">
                                      {u.email}
                                    </span>
                                  </a>
                                ) : (
                                  <span className="text-gray-400">—</span>
                                )}
                              </Table.Cell>

                              <Table.Cell className="text-center py-2 font-medium text-gray-900">
                                <span
                                  className="truncate inline-block max-w-[200px] md:max-w-[260px]"
                                  title={customerName}
                                >
                                  {customerName || "-"}
                                </span>
                              </Table.Cell>

                              <Table.Cell className="text-center py-2">
                                <div className="flex gap-2 justify-center items-center flex-wrap">
                                  <Tooltip
                                    id={tooltipId}
                                    content="Editar usuário"
                                  />
                                  <FaRegEdit
                                    data-tooltip-id={tooltipId}
                                    fontSize="18"
                                    onClick={() => handleEditCustomer(u)}
                                    className="text-gray-500 hover:text-gray-800 transition-colors cursor-pointer"
                                    aria-label={`Editar ${
                                      u?.name ?? "usuário"
                                    }`}
                                  />

                                  {u?.id != 1 && (
                                    <>
                                      <Tooltip
                                        id={impId}
                                        content="Personificar usuário"
                                      />
                                      <button
                                        type="button"
                                        data-tooltip-id={impId}
                                        onClick={() => handleImpersonateUser(u)}
                                        className="rounded px-2 py-1 text-[12px] border border-gray-200 hover:bg-gray-100 flex items-center gap-1"
                                        aria-label={`Personificar ${
                                          u?.name ?? "usuário"
                                        }`}
                                        disabled={impersonatingId === u?.id}
                                      >
                                        {impersonatingId === u?.id ? (
                                          <AiOutlineLoading3Quarters className="animate-spin" />
                                        ) : (
                                          <FaUserShield />
                                        )}
                                        <span>Personificar</span>
                                      </button>
                                    </>
                                  )}
                                </div>
                              </Table.Cell>
                            </Table.Row>
                          );
                        })
                      ) : null
                    ) : (
                      <TableLoading cols={5} />
                    )}
                  </Table.Body>
                </Table>
              </div>
            </div>
          ) : (
            <EmptyTable icon="search_off">
              Nenhum registro encontrado com esses filtros
            </EmptyTable>
          )}

          {totalPages > 1 && (
            <div className="flex overflow-x-auto justify-center mt-3">
              <Pagination
                layout="pagination"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                previousLabel=""
                nextLabel=""
                showIcons
              />
            </div>
          )}
        </div>

        <Modal
          show={isDrawerEditUser}
          onClose={() => setIsDrawerEditUser(false)}
          size="4xl"
        >
          <Modal.Header className="flex items-center py-2">
            <span className="text-base">Editar cliente</span>
          </Modal.Header>
          <Modal.Body>
            <UserForm
              user={selectedUser}
              onClose={setIsDrawerEditUser}
              isOpen={isDrawerEditUser}
              type={"EDIT"}
              fetchUsers={() => {
                queryClient.invalidateQueries(["listUsers"]);
              }}
            />
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default AdminUsersTable;
